import { Suspense, useEffect, useState } from "react";
import {
  BarChart3,
  ChartNoAxesCombined,
  CircleGauge,
  ClipboardPlus,
  FileSearch,
  FolderInput,
  Menu,
  Plus,
  ShieldCheck,
  Sparkles,
  TableProperties,
  Undo2,
  Zap,
} from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import logBookAnaesAppIcon from "../../assets/brand/logbookanaes-app-icon.png";
import { useAuth } from "../providers/AuthProvider";
import { useDemoMode } from "../providers/DemoModeProvider";
import { useWorkspace } from "../providers/WorkspaceProvider";
import { AuthDialog } from "./AuthDialog";

const primaryNavigation = [
  { to: "/", label: "Home", icon: CircleGauge },
  { to: "/cases", label: "Cases", icon: TableProperties },
  { to: "/add", label: "Add Case", icon: ClipboardPlus, shortcut: "A" },
  { to: "/one-click", label: "One Tap Cases", icon: Zap },
  { to: "/import", label: "Import", icon: FolderInput, shortcut: "I" },
  { to: "/reports", label: "Evidence", icon: BarChart3, shortcut: "R" },
];

const insightNavigation = [
  { to: "/progress", label: "Progress", icon: ChartNoAxesCombined },
  { to: "/data-quality", label: "Data Quality", icon: ShieldCheck },
];

export function AppShell() {
  const navigate = useNavigate();
  const { user, entitlement } = useAuth();
  const { enabled: demoMode } = useDemoMode();
  const { error, notice, undoLast, clearNotice } = useWorkspace();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [compactNavigation, setCompactNavigation] = useState(() => matchesCompactNavigation());
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(max-width: 900px)");
    const updateNavigationMode = () => {
      setCompactNavigation(mediaQuery.matches);
      if (!mediaQuery.matches) setDrawerOpen(false);
    };
    updateNavigationMode();
    mediaQuery.addEventListener("change", updateNavigationMode);
    return () => mediaQuery.removeEventListener("change", updateNavigationMode);
  }, []);

  useEffect(() => {
    const handleKeyboard = (event: KeyboardEvent) => {
      const target = event.target;
      const editing = target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
        || (target instanceof HTMLElement && target.isContentEditable);

      if (!demoMode && entitlement === "pro" && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("studio-save"));
        return;
      }
      if (!demoMode && entitlement === "pro" && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z" && !editing) {
        event.preventDefault();
        void undoLast();
        return;
      }
      if (editing || event.ctrlKey || event.metaKey || event.altKey) return;
      const route = demoMode
        ? { i: "/import", r: "/reports" }[event.key.toLowerCase()]
        : entitlement === "lapsed"
          ? { r: "/reports" }[event.key.toLowerCase()]
        : entitlement === "pro"
          ? { a: "/add", i: "/import", r: "/reports" }[event.key.toLowerCase()]
          : undefined;
      if (route) {
        event.preventDefault();
        void navigate(route);
      }
    };
    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [demoMode, entitlement, navigate, undoLast]);

  const hasReadWorkspace = demoMode || entitlement === "pro" || entitlement === "lapsed";
  const hasWriteWorkspace = !demoMode && entitlement === "pro";

  const renderLinks = (items: typeof primaryNavigation) => (
    <ul className="nav-list">
      {items.map(({ to, label, icon: Icon, shortcut }) => (
        <li key={to}>
          <NavLink className={to === "/add" ? "nav-link nav-link-primary" : "nav-link"} end={to === "/"} onClick={() => setDrawerOpen(false)} to={to}>
            <Icon aria-hidden="true" size={18} strokeWidth={1.8} />
            <span>{label}</span>
            {shortcut ? <kbd className="nav-key">{shortcut}</kbd> : null}
          </NavLink>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="app-shell">
      {compactNavigation && drawerOpen ? (
        <button aria-label="Close navigation" className="sidebar-scrim" data-open="true" onClick={() => setDrawerOpen(false)} type="button" />
      ) : null}
      <aside
        aria-hidden={compactNavigation && !drawerOpen ? true : undefined}
        className="app-sidebar"
        data-open={drawerOpen}
        id="studio-navigation"
        inert={compactNavigation && !drawerOpen ? true : undefined}
      >
        <NavLink className="brand-lockup" to="/">
          <img alt="" aria-hidden="true" className="brand-mark" src={logBookAnaesAppIcon} />
          <span>
            <span className="brand-name">LogBookAnaes</span>
            <span className="brand-product">Studio</span>
          </span>
        </NavLink>
        <div className="sidebar-scroll">
          <nav aria-label="Main navigation" className="nav-group">
            <p className="nav-group-label">Workspace</p>
            {renderLinks(demoMode
              ? primaryNavigation.filter(({ to }) => ["/", "/cases", "/import", "/reports"].includes(to))
              : entitlement === "lapsed"
                ? primaryNavigation.filter(({ to }) => ["/", "/cases", "/reports"].includes(to))
              : entitlement === "pro"
                ? primaryNavigation
                : primaryNavigation.filter(({ to }) => to === "/"))}
          </nav>
          {hasReadWorkspace ? <nav aria-label="Insights navigation" className="nav-group">
            <p className="nav-group-label">Review</p>
            {renderLinks(insightNavigation)}
          </nav> : null}
        </div>
        {user && hasWriteWorkspace ? (
          <button className="sidebar-account sidebar-undo" onClick={() => void undoLast()} type="button">
            <span className="account-avatar"><Undo2 aria-hidden="true" size={16} /></span>
            <span className="account-copy">
              <span className="account-name">Undo last change</span>
              <span className="account-meta">Also ⌘Z or Ctrl+Z</span>
            </span>
          </button>
        ) : null}
        {demoMode ? (
          <a className="sidebar-account" href="/">
            <span className="account-avatar">S</span>
            <span className="account-copy">
              <span className="account-name">Sample workspace</span>
              <span className="account-meta">Open your own logbook</span>
            </span>
            <Plus aria-hidden="true" size={15} />
          </a>
        ) : (
          <button className="sidebar-account" onClick={() => setAuthOpen(true)} type="button">
            <span className="account-avatar">{accountInitials(user?.displayName ?? user?.email)}</span>
            <span className="account-copy">
              <span className="account-name">{user?.displayName || (user ? "Your account" : "Sign in")}</span>
              <span className="account-meta">{user ? (entitlement === "pro" ? "Pro workspace" : entitlement === "lapsed" ? "Read-only workspace" : "Pro required") : "Apple, Google or email"}</span>
            </span>
            {entitlement === "pro" ? <Sparkles aria-hidden="true" size={15} /> : <Plus aria-hidden="true" size={15} />}
          </button>
        )}
      </aside>
      <main className="app-main">
        <div className="mobile-topbar">
          <button
            aria-controls="studio-navigation"
            aria-expanded={drawerOpen}
            aria-label="Open navigation"
            className="icon-button"
            onClick={() => setDrawerOpen(true)}
            type="button"
          ><Menu aria-hidden="true" size={19} /></button>
          <span className="mobile-brand-lockup">
            <img alt="" aria-hidden="true" className="mobile-brand-mark" src={logBookAnaesAppIcon} />
            <span className="brand-name">LogBookAnaes <span className="brand-product">Studio</span></span>
          </span>
          <span className="mobile-topbar-actions">
            {user && hasWriteWorkspace ? (
              <button aria-label="Undo last change" className="account-avatar" onClick={() => void undoLast()} type="button">
                <Undo2 aria-hidden="true" size={16} />
              </button>
            ) : null}
            {demoMode ? <span aria-label="Sample workspace" className="account-avatar">S</span> : <button aria-label="Open account" className="account-avatar" onClick={() => setAuthOpen(true)} type="button">{accountInitials(user?.displayName ?? user?.email)}</button>}
          </span>
        </div>
        {demoMode ? (
          <div className="demo-banner" role="status">
            <span><strong>Sample workspace</strong> · 2,605 fictional cases · Changes are not saved · Downloads contain sample data only</span>
            <a className="demo-banner-link" href="/">Open my logbook</a>
          </div>
        ) : entitlement === "lapsed" ? (
          <div className="demo-banner" role="status">
            <span><strong>Read-only workspace</strong> · Your Pro access has ended · You can review and export your existing data</span>
            <a className="demo-banner-link" href="https://app.logbookanaes.com" rel="noreferrer" target="_blank">Renew Pro</a>
          </div>
        ) : null}
        {error ? <div className="global-banner" role="alert"><FileSearch size={16} /> {error}</div> : null}
        <Suspense fallback={<div className="route-loading" role="status">Opening workspace…</div>}>
          <Outlet context={{ openAuth: () => setAuthOpen(true) }} />
        </Suspense>
      </main>
      {notice ? (
        <div className="toast" role="status">
          <span className="toast-message">{notice.message}</span>
          {notice.actionLabel ? <button className="btn btn-quiet" onClick={() => void undoLast()} type="button">{notice.actionLabel}</button> : null}
          <button aria-label="Dismiss notification" className="link-button" onClick={clearNotice} type="button">Dismiss</button>
        </div>
      ) : null}
      {demoMode ? null : <AuthDialog onClose={() => setAuthOpen(false)} open={authOpen} />}
    </div>
  );
}

function accountInitials(value: string | null | undefined): string {
  if (!value) return "LB";
  return value.split(/\s+|@/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "LB";
}

function matchesCompactNavigation(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(max-width: 900px)").matches;
}
